// src/components/FullBingoBoard.jsx
import React from "react";

// Helper to determine the letter for a given number (B-I-N-G-O)
const getBallLetter = (num) => {
  if (num >= 1 && num <= 15) return "B";
  if (num >= 16 && num <= 30) return "I";
  if (num >= 31 && num <= 45) return "N";
  if (num >= 46 && num <= 60) return "G";
  if (num >= 61 && num <= 75) return "O";
  return "";
};

const FullBingoBoard = ({ calledNumbers }) => {
  const letters = ["B", "I", "N", "G", "O"];
  const ranges = [
    { start: 1, end: 15 },
    { start: 16, end: 30 },
    { start: 31, end: 45 },
    { start: 46, end: 60 },
    { start: 61, end: 75 },
  ];

  const getNumberCellClasses = (num, isCalled) => {
    let baseClasses =
      "w-full h-auto aspect-square flex items-center justify-center font-extrabold transition-all duration-500 ease-in-out font-digital";
    baseClasses += " text-2xl lg:text-3xl p-1 lg:p-2";

    if (isCalled) {
      baseClasses += " bg-red-600 text-white shadow-inner animate-[popAndRotate_0.5s_ease-in-out]";
    } else {
      baseClasses += " bg-[#1A1A1A] text-[#CCCCCC]";
    }
    return baseClasses;
  };

  return (
    <div className="p-4 bg-black rounded-lg shadow-xl border border-gray-700 max-w-full mx-auto overflow-x-auto">
      {/* Add the animation keyframes */}
      <style>{`
        @keyframes popAndRotate {
          0% {
            transform: scale(0.8) rotate(-10deg);
            opacity: 0;
          }
          50% {
            transform: scale(1.1) rotate(360deg);
          }
          100% {
            transform: scale(1) rotate(0deg);
            opacity: 1;
          }
        }
      `}</style>

      {/* Rest of the component remains the same... */}
      <div
        className="hidden lg:grid"
        style={{
          gridTemplateColumns: "minmax(min-content, auto) repeat(15, minmax(0, 1fr))",
          gap: "2px",
          fontFamily: "'Digital-7 Mono', 'Courier New', monospace",
        }}
      >
        {/* Vertical BINGO letters */}
        {letters.map((letter, letterIndex) => (
          <div
            key={`desktop-letter-${letter}`}
            className={`flex items-center justify-center font-extrabold text-4xl text-red-600 px-2 py-1`}
            style={{
              gridColumn: "1",
              gridRow: letterIndex + 1,
              backgroundColor: '#FFFFFF',
              borderRight: '1px solid #333',
              minWidth: '40px',
            }}
          >
            {letter}
          </div>
        ))}

        {/* Numbers Grid */}
        {Array.from({ length: 75 }, (_, i) => i + 1).map((num) => {
          const isCalled = calledNumbers.includes(num);
          const colIndex = (num - 1) % 15;
          const rowIndex = Math.floor((num - 1) / 15);

          return (
            <div
              key={`desktop-num-${num}-${isCalled}`}
              className={getNumberCellClasses(num, isCalled)}
              style={{
                gridColumn: colIndex + 2,
                gridRow: rowIndex + 1,
              }}
            >
              {num}
            </div>
          );
        })}
      </div>

      {/* MOBILE View */}
      <div className="lg:hidden flex flex-col items-center">
        {/* Header: BINGO letters horizontal */}
        <div className="grid grid-cols-5 gap-1 mb-2 px-1 w-full max-w-[300px]">
          {letters.map((letter) => (
            <div
              key={`mobile-header-${letter}`}
              className="flex items-center justify-center font-extrabold text-2xl text-red-600 px-1 py-0.5"
              style={{
                backgroundColor: '#FFFFFF',
              }}
            >
              {letter}
            </div>
          ))}
        </div>

        {/* Numbers Grid */}
        <div
          className="grid grid-cols-5 gap-1 w-full max-w-[300px]"
          style={{
            fontFamily: "'Digital-7 Mono', 'Courier New', monospace",
          }}
        >
          {Array.from({ length: 15 }).map((_, rowIndex) => (
            <React.Fragment key={`mobile-row-${rowIndex}`}>
              {letters.map((letter, colIndex) => {
                const num = ranges[colIndex].start + rowIndex;
                const isCalled = calledNumbers.includes(num);
                if (num <= ranges[colIndex].end) {
                  return (
                    <div
                      key={`mobile-num-${letter}-${num}-${isCalled}`}
                      className={getNumberCellClasses(num, isCalled)}
                    >
                      {num}
                    </div>
                  );
                }
                return (
                  <div
                    key={`mobile-empty-${letter}-${rowIndex}`}
                    className="w-full h-auto aspect-square bg-[#1A1A1A]"
                  ></div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FullBingoBoard;